import { useCallback, useEffect, useState } from "react";
import type { WorkItemActivityDto } from "@yagni/shared";

interface UseWorkItemActivityResult {
  activity: WorkItemActivityDto[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useWorkItemActivity(
  workItemId: string,
): UseWorkItemActivityResult {
  const [activity, setActivity] = useState<WorkItemActivityDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`/api/work-items/${encodeURIComponent(workItemId)}/activity`, {
      credentials: "include",
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`activity fetch failed: ${res.status}`);
        return res.json();
      })
      .then((data: { activity: WorkItemActivityDto[] }) => {
        if (cancelled) return;
        setActivity(data.activity ?? []);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workItemId]);

  useEffect(() => {
    return refresh();
  }, [refresh]);

  // Resync (P1.4): an activity broadcast missed while the socket was down or
  // the tab was hidden never arrives, so refetch the timeline when a socket
  // reconnects (`ws.reconnected` from useWebSocket) and when the tab becomes
  // visible again.
  useEffect(() => {
    // Only the newest resync may land: cancel the previous one first.
    let cancelLast: (() => void) | undefined;
    const resync = () => {
      cancelLast?.();
      cancelLast = refresh();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") resync();
    };
    window.addEventListener("ws.reconnected", resync);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelLast?.();
      window.removeEventListener("ws.reconnected", resync);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refresh]);

  // Live updates via window event proxied from the WS layer.
  // `useWebSocket` dispatches a CustomEvent named `work_item_activity.created`
  // carrying the WS payload `{ workItemId, activity }` when the server
  // broadcasts. We listen here and append to local state, deduping by id so
  // repeated deliveries (reconnect, double-subscribe) can't produce duplicates.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          workItemId: string;
          activity: WorkItemActivityDto;
        }>
      ).detail;
      if (!detail || detail.workItemId !== workItemId) return;
      setActivity((prev) => {
        if (prev.some((a) => a.id === detail.activity.id)) return prev;
        const next = [...prev, detail.activity];
        next.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
        return next;
      });
    };
    window.addEventListener("work_item_activity.created", handler);
    return () =>
      window.removeEventListener("work_item_activity.created", handler);
  }, [workItemId]);

  return { activity, loading, error, refresh };
}
